import {isMatch} from 'lodash'
import createClient from 'openapi-fetch'
import type {HttpMethod, PathsWithMethod, RequiredKeysOf} from 'openapi-typescript-helpers'
import {createQueryHook, createImmutableHook, createMutateHook} from 'swr-openapi'
import {type TypesForGetRequest} from 'swr-openapi'
import {
  mkWrappedFetch,
  mkRequestInit,
  ajaxJsonCall
} from '@freckle/student-entities/ts/common/helpers/ajax'
import {type EchoError} from '@freckle/student-entities/ts/common/helpers/api-helper'
import CApiHelper from '@freckle/student-entities/ts/common/helpers/common-api-helper'
import type {paths} from '@freckle/student-entities/ts/open-api/fancy/generated-client'
import {PATHS} from '@freckle/student-materials/src/helpers/paths'
import {logErrorAsUnhandled} from '@freckle/student/ts/common/helpers/exception-handlers/bugsnag-client'
import {resetAfterSignout} from '@freckle/student/ts/common/routers/init'
import {reportActionableError} from '../helpers/error-reporting'
import {fixErrorStack} from '../helpers/error-stack'
import type {ResponseData, ResponseDatum} from '../helpers/response'

const PREFIX = 'fancy-api'

export function onEchoError(echoError: EchoError) {
  logErrorAsUnhandled(echoError)
  deleteSessionCookie().then(() => {
    resetAfterSignout()
  })
}

export function deleteSessionCookie(): Promise<{
  redirectUrl?: string
}> {
  return ajaxJsonCall({
    url: CApiHelper.fancyPaths.v2.students.sessions(),
    method: 'DELETE'
  })
}

const wrappedFetch = mkWrappedFetch(onEchoError)

export const client = createClient<paths>({
  baseUrl: PATHS.unversionedAPIUrl,
  credentials: 'include',
  fetch: async (input: Request) => wrappedFetch(input.url, await mkRequestInit(input))
})

export type FancyResponseData<P extends keyof paths> = ResponseData<
  paths,
  P,
  'get',
  200,
  'application/json'
>

export type FancyResponseDatum<
  P extends keyof paths,
  method extends HttpMethod = 'get'
> = ResponseDatum<paths, P, method, 200, 'application/json'>

const _useApiQuery = createQueryHook(client, PREFIX)
const _useApiImmutable = createImmutableHook(client, PREFIX)
export const useApiMutate = createMutateHook(client, PREFIX, isMatch)

export const useApiQuery = <
  Path extends PathsWithMethod<paths, 'get'>,
  R extends TypesForGetRequest<paths, Path>,
  Init extends R['Init'],
  Config extends R['SWRConfig']
>(
  path: Path,
  ...[init, config, framesToRemove]: RequiredKeysOf<Init> extends never
    ? [(Init | null)?, Config?, number?]
    : [Init | null, Config?, number?]
) => {
  const stack = new Error().stack
  // @ts-expect-error: Types taken from swr-openapi will
  // probably be improved in the future version of the library
  const {error: e, ...rest} = _useApiQuery(path, init, config)
  const error = fixErrorStack(e, stack, 2 + (framesToRemove ?? 0))
  reportActionableError(error)
  return {error, ...rest}
}

export const useApiImmutable = <
  Path extends PathsWithMethod<paths, 'get'>,
  R extends TypesForGetRequest<paths, Path>,
  Init extends R['Init'],
  Config extends R['SWRConfig']
>(
  path: Path,
  ...[init, config, framesToRemove]: RequiredKeysOf<Init> extends never
    ? [(Init | null)?, Config?, number?]
    : [Init | null, Config?, number?]
) => {
  const stack = new Error().stack
  // @ts-expect-error: Types taken from swr-openapi will
  // probably be improved in the future version of the library
  const {error: e, ...rest} = _useApiImmutable(path, init, config)
  const error = fixErrorStack(e, stack, 2 + (framesToRemove ?? 0))
  reportActionableError(error)
  return {error, ...rest}
}
